<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { SEEK_SECONDS, useAudioPlayerStore, type SleepMode } from '@/stores/audioPlayer'
import { useLibraryStore } from '@/stores/library'

/**
 * Mini-barra del reproductor de audiolibro.
 *
 * Fija abajo y visible en cualquier ruta MENOS el lector: allí manda
 * `TtsControls` y duplicar controles confunde. Lee el store directamente
 * (no props): es un singleton de UI, no un componente reutilizable.
 *
 * La condición de visibilidad vive en el store (`isMiniBarVisible`), no
 * aquí: `Toaster` la necesita también para no dibujar los toasts encima de
 * los controles, y duplicarla en dos componentes las desincronizaría.
 */
const player = useAudioPlayerStore()
const library = useLibraryStore()
const router = useRouter()

const book = computed(() => (player.bookId ? library.find(player.bookId) : undefined))
const visible = computed(() => player.isMiniBarVisible)

/**
 * ¿Hay escucha en marcha, sonando o viniendo de camino? 'loading' cuenta: la
 * reproducción ya está pedida y lo que ofrece el botón es cancelarla
 * (`pause()` deja obsoleta la carga en vuelo), así que el icono tiene que ser
 * el de pausa. Con el ▶ el control mentía —decía «no suena, púlsame para
 * empezar» cuando ya se estaba empezando— y encima no hacía nada.
 */
const isActive = computed(() => player.status === 'playing' || player.status === 'loading')

// Mide la altura real de la barra (varía con el breakpoint: el selector de
// velocidad solo se ve desde `sm`) para que `Toaster` desplace los toasts
// justo por encima, sin un número inventado que pueda quedarse corto.
const barEl = ref<HTMLElement | null>(null)
let resizeObserver: ResizeObserver | null = null

watch(barEl, (el) => {
  resizeObserver?.disconnect()
  resizeObserver = null
  if (!el) return
  resizeObserver = new ResizeObserver(() => player.setMiniBarHeight(el.offsetHeight))
  resizeObserver.observe(el)
})

onBeforeUnmount(() => resizeObserver?.disconnect())

/**
 * ¿Puede la mini-barra ofrecer play/pausa ahora mismo? No solo cuando hay
 * escucha en marcha: para ARRANCAR también hace falta un motor conectado para
 * `engineId`. Sin él (p. ej. tras salir del lector con el motor navegador, que
 * `ReaderView` desconecta al desmontarse) el botón no tendría con qué cumplir
 * — mejor deshabilitarlo que dejarlo silenciosamente muerto.
 */
const canControl = computed(() => isActive.value || player.engineConnected)

/**
 * Anterior/siguiente, además, NO mientras se prepara la cola ('loading'):
 * ahí los fragmentos que el motor tiene en memoria son todavía los del libro
 * ANTERIOR, y saltar de uno a otro sintetizaría —dinero real— el libro
 * equivocado. El motor se defiende solo (no reproduce una cola que no sea la
 * del libro pedido), pero un botón activo que no hace nada es peor que uno
 * deshabilitado con «Preparando…» al lado.
 */
const canSkip = computed(() => player.engineConnected && player.status !== 'loading')

/**
 * ±15 s: además de la cola preparada, hace falta un motor que sepa saltar.
 * Con la voz del navegador no lo hay (`speechSynthesis` no expone posición),
 * así que los botones se deshabilitan y su `title`/`aria-label` pasa a decir
 * POR QUÉ — un ±15 s activo que no mueve nada es peor que uno apagado.
 */
const canSeek = computed(() => player.seekSupported && player.status !== 'loading')

/**
 * Clave del motivo por el que el salto está deshabilitado, o `null` si se
 * puede. Bifurca por la CAUSA: son dos, y con un solo texto el control mentía
 * —«cambia a neural» con el motor neural ya activo y la cola preparándose—,
 * que es justo el tipo de control deshonesto que este ±15 s venía a evitar.
 */
const seekDisabledReason = computed(() => {
  if (!player.seekSupported) return 'audio.seekUnavailable'
  if (player.status === 'loading') return 'audio.preparing'
  return null
})

function togglePlay() {
  if (isActive.value) player.pause()
  else if (player.engineConnected) void player.resume()
}

function openInReader() {
  if (player.bookId) void router.push({ name: 'reader', params: { bookId: player.bookId } })
}

/** Valor del select: 'off' | 'chapter' | los minutos como string. */
const sleepValue = computed(() =>
  player.sleepMode === 'minutes' ? String(player.sleepMinutes) : player.sleepMode,
)

function onSleepChange(value: string) {
  if (value === 'off' || value === 'chapter') {
    player.setSleepTimer(value as SleepMode)
    return
  }
  player.setSleepTimer('minutes', Number(value))
}
</script>

<template>
  <Transition name="slide-up">
    <div
      v-if="visible"
      ref="barEl"
      class="fixed bottom-0 left-0 right-0 z-40 flex items-center gap-3 border-t border-surface-muted/60 bg-surface/95 px-3 py-2 shadow-2xl backdrop-blur"
      role="group"
      :aria-label="$t('audio.miniBar')"
    >
      <!-- Título del libro -->
      <button
        type="button"
        class="min-w-0 flex-1 text-left"
        :title="$t('audio.openInReader')"
        :aria-label="$t('audio.openInReader')"
        @click="openInReader"
      >
        <span class="block truncate text-sm text-ink">{{ book?.title ?? '' }}</span>
        <span class="block truncate text-xs text-ink-muted">
          {{ player.status === 'loading' ? $t('audio.preparing') : (book?.author ?? '') }}
        </span>
      </button>

      <!-- Anterior -->
      <button
        type="button"
        class="btn-press min-h-9 min-w-9 rounded-full p-1.5 text-ink-subtle hover:bg-surface-muted/50 hover:text-ink disabled:opacity-40"
        :disabled="!canSkip"
        :title="$t('audio.prev')"
        :aria-label="$t('audio.prev')"
        @click="player.prev()"
      >
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" /></svg>
      </button>

      <!-- Retroceder 15 s -->
      <button
        type="button"
        class="btn-press min-h-9 min-w-9 rounded-full p-1.5 text-ink-subtle hover:bg-surface-muted/50 hover:text-ink disabled:opacity-40"
        :disabled="!canSeek"
        :title="seekDisabledReason ? $t(seekDisabledReason) : $t('audio.seekBack', { n: SEEK_SECONDS })"
        :aria-label="seekDisabledReason ? $t(seekDisabledReason) : $t('audio.seekBack', { n: SEEK_SECONDS })"
        @click="player.seek(-SEEK_SECONDS)"
      >
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
          <path stroke-linecap="round" d="M12 7a6 6 0 1 0 4.5 2" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 4l-3 3 3 3" />
          <text x="12" y="16.5" text-anchor="middle" font-size="8" stroke="none" fill="currentColor">15</text>
        </svg>
      </button>

      <!-- Play / Pausa -->
      <button
        type="button"
        class="btn-press rounded-full bg-accent p-2 text-accent-fg hover:brightness-110 disabled:opacity-40"
        :disabled="!canControl"
        :title="canControl ? (isActive ? $t('audio.pause') : $t('audio.play')) : $t('audio.resumeUnavailable')"
        :aria-label="canControl ? (isActive ? $t('audio.pause') : $t('audio.play')) : $t('audio.resumeUnavailable')"
        @click="togglePlay"
      >
        <svg v-if="isActive" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zm8 0h4v14h-4z" /></svg>
        <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
      </button>

      <!-- Avanzar 15 s -->
      <button
        type="button"
        class="btn-press min-h-9 min-w-9 rounded-full p-1.5 text-ink-subtle hover:bg-surface-muted/50 hover:text-ink disabled:opacity-40"
        :disabled="!canSeek"
        :title="seekDisabledReason ? $t(seekDisabledReason) : $t('audio.seekForward', { n: SEEK_SECONDS })"
        :aria-label="seekDisabledReason ? $t(seekDisabledReason) : $t('audio.seekForward', { n: SEEK_SECONDS })"
        @click="player.seek(SEEK_SECONDS)"
      >
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
          <path stroke-linecap="round" d="M12 7a6 6 0 1 1-4.5 2" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 4l3 3-3 3" />
          <text x="12" y="16.5" text-anchor="middle" font-size="8" stroke="none" fill="currentColor">15</text>
        </svg>
      </button>

      <!-- Siguiente -->
      <button
        type="button"
        class="btn-press min-h-9 min-w-9 rounded-full p-1.5 text-ink-subtle hover:bg-surface-muted/50 hover:text-ink disabled:opacity-40"
        :disabled="!canSkip"
        :title="$t('audio.next')"
        :aria-label="$t('audio.next')"
        @click="player.next()"
      >
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zM16 6h2v12h-2z" /></svg>
      </button>

      <!-- Velocidad -->
      <label class="hidden items-center gap-1 text-[0.7rem] text-ink-muted sm:flex" :title="$t('audio.rate')">
        <select
          class="rounded bg-surface-subtle px-1 py-0.5 text-xs text-ink focus:outline-none"
          :value="player.rate"
          :aria-label="$t('audio.rate')"
          @change="player.setRate(Number(($event.target as HTMLSelectElement).value))"
        >
          <option :value="0.75">0.75×</option>
          <option :value="1">1×</option>
          <option :value="1.25">1.25×</option>
          <option :value="1.5">1.5×</option>
          <option :value="2">2×</option>
        </select>
      </label>

      <!-- Temporizador de sueño -->
      <label class="hidden items-center gap-1 text-[0.7rem] text-ink-muted sm:flex" :title="$t('audio.sleep')">
        <select
          class="rounded bg-surface-subtle px-1 py-0.5 text-xs text-ink focus:outline-none"
          :value="sleepValue"
          :aria-label="$t('audio.sleep')"
          @change="onSleepChange(($event.target as HTMLSelectElement).value)"
        >
          <option value="off">{{ $t('audio.sleepOff') }}</option>
          <option value="15">{{ $t('audio.sleepMinutes', { n: 15 }) }}</option>
          <option value="30">{{ $t('audio.sleepMinutes', { n: 30 }) }}</option>
          <option value="45">{{ $t('audio.sleepMinutes', { n: 45 }) }}</option>
          <option value="60">{{ $t('audio.sleepMinutes', { n: 60 }) }}</option>
          <option value="chapter">{{ $t('audio.sleepChapter') }}</option>
        </select>
      </label>

      <!-- Cerrar -->
      <button
        type="button"
        class="btn-press min-h-9 min-w-9 rounded-full p-1.5 text-ink-muted hover:bg-red-500/15 hover:text-red-500"
        :title="$t('audio.stop')"
        :aria-label="$t('audio.stop')"
        @click="player.stop()"
      >
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
